import type React from 'react';
import { cn } from '@/lib/utils';

interface TableProps {
  children: React.ReactNode;
  className?: string;
}

interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface TableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

interface TableCellProps {
  children: React.ReactNode;
  className?: string;
}

const Table: React.FC<TableProps> & {
  Header: React.FC<TableHeaderProps>;
  Body: React.FC<TableBodyProps>;
  Row: React.FC<TableRowProps>;
  Head: React.FC<TableCellProps>;
  Cell: React.FC<TableCellProps>;
} = ({ children, className }) => (
  <div className={cn('overflow-x-auto rounded-xl border border-gray-100', className)}>
    <table className="w-full text-sm text-left">
      {children}
    </table>
  </div>
);

const TableHeader: React.FC<TableHeaderProps> = ({ children, className }) => (
  <thead className={cn('bg-gray-50 text-gray-600 text-xs uppercase tracking-wider', className)}>
    {children}
  </thead>
);

const TableBody: React.FC<TableBodyProps> = ({ children, className }) => (
  <tbody className={cn('divide-y divide-gray-100', className)}>
    {children}
  </tbody>
);

const TableRow: React.FC<TableRowProps> = ({ children, className, onClick }) => (
  <tr
    className={cn('hover:bg-gray-50/50 transition-colors', onClick && 'cursor-pointer', className)}
    onClick={onClick}
  >
    {children}
  </tr>
);

const TableHead: React.FC<TableCellProps> = ({ children, className }) => (
  <th className={cn('px-4 py-3 font-semibold', className)}>
    {children}
  </th>
);

const TableCell: React.FC<TableCellProps> = ({ children, className }) => (
  <td className={cn('px-4 py-3 text-gray-700', className)}>
    {children}
  </td>
);

Table.Header = TableHeader;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Head = TableHead;
Table.Cell = TableCell;

export default Table;
